import gql from 'graphql-tag';
import { UPSParcel } from '../../Users/query';

export interface DateFilters {
  // Date range filters (ISO date strings)
  createdAtFrom?: string;
  createdAtTo?: string;
  updatedAtFrom?: string;
  updatedAtTo?: string;
  shipperDropOffAtFrom?: string;
  shipperDropOffAtTo?: string;
  driverDropOffAtFrom?: string;
  driverDropOffAtTo?: string;
  consigneePickupAtFrom?: string;
  consigneePickupAtTo?: string;
  parcelAgedAtFrom?: string;
  parcelAgedAtTo?: string;
  dispossessedAtFrom?: string;
  dispossessedAtTo?: string;
  // Boolean filters for existence of scan events
  hasDriverDropOff?: boolean;
  hasConsigneePickup?: boolean;
  hasShipperDropOff?: boolean;
  hasParcelAged?: boolean;
}

export type UPSParcelList = {
  parcels: UPSParcel[];
  totalCount: number;
  limit: number;
  offset: number;
  hasMore: boolean;
};

export type InternalUPSParcelStatistics = {
  totalParcels: number;
  totalAccessPoints: number;
  activeAccessPoints: number;
  averageParcelsPerAccessPoint: number;
  dispossessionsThisMonth: number;
  dispossessionsLastMonth: number;
  byStatus: {
    status: string;
    count: number;
    percentage: number;
  }[];
  byAccessPoint: {
    accessPointID: string;
    count: number;
  }[];
  historicalData: {
    period: string;
    count: number;
    date: string;
  }[];
  scanEventStats: {
    driverDropOffs: number;
    driverDropOffAcks: number;
    consigneePickups: number;
    consigneePickupAcks: number;
    shipperDropOffs: number;
    shipperDropOffAcks: number;
    agedParcels: number;
    agedParcelScans: number;
    driverDropOffAckRate: number;
    consigneePickupAckRate: number;
    shipperDropOffAckRate: number;
  };
  timeRange: string;
  startDate: string;
  endDate: string;
};

export type InternalAllUPSParcelsQuery = {
  internalAllUPSParcels: UPSParcelList;
};

export type InternalUPSParcelStatisticsQuery = {
  internalUPSParcelStatistics: InternalUPSParcelStatistics;
};

export type ParcelSortField =
  | 'trackingNumber'
  | 'createdAt'
  | 'updatedAt'
  | 'shipperDropOffAt'
  | 'shipperDropOffAckAt'
  | 'driverDropOffAt'
  | 'driverDropOffAckAt'
  | 'consigneePickupAt'
  | 'consigneePickupAckAt'
  | 'parcelAgedAt'
  | 'agedParcelScanAt'
  | 'agedParcelScanAckAt'
  | 'consigneeName'
  | 'accessPointID'
  | 'deliveryStatus';

export const GET_PARCELS = gql`
  query InternalAllUPSParcels(
    $accessPointIDs: [String!]
    $statuses: [UPSQueryType!]
    $dateFilters: UPSDateFiltersInput
    $parcelDetails: UPSParcelDetailsInput
    $trackingNumberSearch: String
    $sortBy: UPSParcelSortField
    $sortOrder: SortOrder
    $limit: Int
    $offset: Int
  ) {
    internalAllUPSParcels(
      accessPointIDs: $accessPointIDs
      statuses: $statuses
      dateFilters: $dateFilters
      parcelDetails: $parcelDetails
      trackingNumberSearch: $trackingNumberSearch
      sortBy: $sortBy
      sortOrder: $sortOrder
      limit: $limit
      offset: $offset
    ) {
      parcels {
        accessPointID
        deliveryStatus
        trackingNumber
        consigneeName
        epraIndicator
        addresseeOnlyIndicator
        adultSignatureIndicator
        consigneePickupAt
        consigneePickupAckAt
        driverDropOffAt
        driverDropOffAckAt
        parcelAgedAt
        agedParcelScanAt
        agedParcelScanAckAt
        shipperDropOffAt
        shipperDropOffAckAt
        updatedAt
        createdAt
        userId
        notInInventorySync
        hiddenInApp
      }
      totalCount
      limit
      offset
      hasMore
    }
  }
`;

export const GET_PARCEL_STATISTICS = gql`
  query InternalUPSParcelStatistics {
    internalUPSParcelStatistics {
      totalParcels
      totalAccessPoints
      activeAccessPoints
      averageParcelsPerAccessPoint

      dispossessionsThisMonth
      dispossessionsLastMonth

      byStatus {
        status
        count
        percentage
      }

      byAccessPoint {
        accessPointID
        count
      }

      historicalData {
        period
        count
        date
      }

      scanEventStats {
        driverDropOffs
        driverDropOffAcks
        consigneePickups
        consigneePickupAcks
        shipperDropOffs
        shipperDropOffAcks
        agedParcels
        agedParcelScans
        driverDropOffAckRate
        consigneePickupAckRate
        shipperDropOffAckRate
      }

      timeRange
      startDate
      endDate
    }
  }
`;

export const INTERNAL_CONSIGNEE_SCAN = gql`
  mutation InternalConsigneePickupScan(
    $trackingNumber: String!
    $accessPointID: String!
    $comment: String!
    $internalUserId: String!
    $pin: String
  ) {
    internalConsigneePickupScan(
      trackingNumber: $trackingNumber
      accessPointID: $accessPointID
      comment: $comment
      internalUserId: $internalUserId
      pin: $pin
    )
  }
`;

export const INTERNAL_DRIVER_DROP_OFF_SCAN = gql`
  mutation InternalDriverDropOffScan(
    $trackingNumber: String!
    $accessPointID: String!
    $comment: String!
    $internalUserId: String!
  ) {
    internalDriverDropOffScan(
      trackingNumber: $trackingNumber
      accessPointID: $accessPointID
      comment: $comment
      internalUserId: $internalUserId
    )
  }
`;

export const INTERNAL_AGED_PARCEL_SCAN = gql`
  mutation InternalAgedParcelScan(
    $trackingNumber: String!
    $accessPointID: String!
    $comment: String!
    $internalUserId: String!
  ) {
    internalAgedParcelScan(
      trackingNumber: $trackingNumber
      accessPointID: $accessPointID
      comment: $comment
      internalUserId: $internalUserId
    )
  }
`;
